import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import {
  type BffCertificateModelWithPermissions,
  CertificateOnlyApi,
  type CertificateOnlyApiCreateCertificateOnlyRequest,
  type CertificateOnlyApiUpdateCertificateOnlyPolicyRequest,
  type CertificateOnlyApiUploadCertificateOnlyFileRequest,
} from '../../generated/api-client';
import { type ErrorResponse } from '../../network/apis/types';
import { getBffApiConfiguration } from '../../network/AuthRequests';
import { arrayBufferToBase64 } from '../../utils/file';

export const useCreateCertificate = ({
  options,
}: {
  options?: UseMutationOptions<
    BffCertificateModelWithPermissions,
    ErrorResponse,
    CertificateOnlyApiCreateCertificateOnlyRequest
  >;
}) => {
  return useMutation({
    mutationFn: async (payload) => {
      const apiConfig = await getBffApiConfiguration();
      return (
        await new CertificateOnlyApi(apiConfig).createCertificateOnly(payload)
      ).data;
    },
    ...options,
  });
};

export const useUpdateCertificate = ({
  options,
}: {
  options?: UseMutationOptions<
    BffCertificateModelWithPermissions,
    ErrorResponse,
    CertificateOnlyApiUpdateCertificateOnlyPolicyRequest
  >;
}) => {
  return useMutation({
    mutationFn: async (payload) => {
      const apiConfig = await getBffApiConfiguration();
      return (
        await new CertificateOnlyApi(apiConfig).updateCertificateOnlyPolicy(
          payload,
        )
      ).data;
    },
    ...options,
  });
};

export const useUploadCertificateFile = ({
  options,
}: {
  options?: UseMutationOptions<
    void,
    {
      message: string;
      status: number;
      data: unknown;
      fileName?: string;
    },
    CertificateOnlyApiUploadCertificateOnlyFileRequest
  >;
}) => {
  return useMutation({
    mutationFn: async (payload) => {
      try {
        const apiConfig = await getBffApiConfiguration();
        const fileBytes =
          await payload?.bffUploadCertificateOnlyFilePayload?.content.arrayBuffer();
        if (!fileBytes) {
          throw new Error('File content is not available');
        }
        const base64Content = arrayBufferToBase64(fileBytes);
        return (
          await new CertificateOnlyApi(apiConfig).uploadCertificateOnlyFile({
            ...payload,
            bffUploadCertificateOnlyFilePayload: {
              ...payload.bffUploadCertificateOnlyFilePayload,
              // @ts-expect-error -- backend expects bytes, but actual type is base64 string
              content: base64Content,
            },
          })
        ).data;
      } catch (e) {
        throw {
          message: 'Error uploading file',
          status: 500,
          data: e,
          fileName: payload.bffUploadCertificateOnlyFilePayload?.content.name,
        };
      }
    },
    ...options,
  });
};
